/**
 * Invoicing Page
 * NetSuite-backed invoicing with a local draft approval queue.
 * Invoices are saved as Drafts locally first, then approved and sent to NetSuite.
 * Gated by canAccessInvoicing flag on the user object.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2, AlertCircle, CheckCircle, X, Plus, Trash2, Printer,
  Search, ChevronDown, ChevronRight, Receipt, Building2, User, FileText,
  Clock, BarChart3, RefreshCw, CheckSquare, Wand2, Filter, Mail, PlayCircle,
} from 'lucide-react';
import Navigation from '../components/Navigation';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import ConfirmDialog, { useConfirm } from '../components/ConfirmDialog';
import { useAuth } from '../context/AuthContext';
import {
  checkHealth,
  getGovernanceLimits,
  getAllCustomers,
  searchCustomers,
  getCustomer,
  getCustomerAddress,
  createCustomer,
  getInvoice,
  getBillableItems,
  getDepartments,
  getLocations,
  getAasPriceForNsCustomer,
  Customer,
  CustomerSummary,
  Invoice,
  BillableItem,
  NetSuiteDepartment,
  NetSuiteLocation,
  INVOICE_TERMS,
  CreateCustomerRequest,
  CustomerAddress,
} from '../services/netsuiteService';
import {
  createDraftInvoice,
  listInvoices,
  approveInvoice,
  deleteInvoice,
  getRunningTotals,
  getMonthlyActivity,
  sendInvoiceEmail,
  triggerAutoInvoicing,
  LocalInvoice,
  ClientRunningTotal,
  MonthlyActivity,
} from '../services/invoicesService';
import { getErrorMessage } from '../services/apiService';
import { formatPhone, maskPhoneInput } from '../utils/validation';

// --------------------------------------------------------------------------
// Helpers
// --------------------------------------------------------------------------

const fmt = (n?: number): string =>
  n === undefined || n === null
    ? '$0.00'
    : n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const fmtQty = (n?: number): string =>
  n === undefined || n === null ? '0' : n.toLocaleString('en-US');

const fmtDate = (s?: string): string => {
  if (!s) return '';
  try {
    const d = new Date(s);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return s;
  }
};

const fmtDateShort = (s?: string): string => {
  if (!s) return '';
  try {
    // Parse as local date if YYYY-MM-DD to avoid UTC offset issues
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const [y, m, d] = s.split('-').map(Number);
      return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
    return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return s;
  }
};

const fmtDateTime = (s?: string): string => {
  if (!s) return '';
  try {
    return new Date(s).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return s;
  }
};

/** True when the given UTC timestamp falls on the viewer's current local day. */
const isToday = (iso?: string): boolean => {
  if (!iso) return false;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return false;
  const now = new Date();
  return d.getFullYear() === now.getFullYear()
    && d.getMonth() === now.getMonth()
    && d.getDate() === now.getDate();
};

const today = (): string => new Date().toISOString().split('T')[0];

// --------------------------------------------------------------------------
// Sub-types for form state
// --------------------------------------------------------------------------

interface LineItemRow {
  itemId: string;
  description: string;
  rate: number;
  quantity: number;
  priceSource?: 'aas' | 'catalog';
}

interface NewCustomerForm {
  isPerson: boolean;
  companyName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

type ActiveTab = 'create' | 'pending' | 'totals' | 'history';

// --------------------------------------------------------------------------
// Component
// --------------------------------------------------------------------------

const Invoicing: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // ---- Tabs ----
  const [activeTab, setActiveTab] = useState<ActiveTab>('create');

  // ---- Health / governance ----
  const [netsuiteStatus, setNetsuiteStatus] = useState<'checking' | 'connected' | 'unreachable'>('checking');
  const [governanceLimits, setGovernanceLimits] = useState<any>(null);

  // ---- Customer selector ----
  const [allCustomers, setAllCustomers] = useState<CustomerSummary[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customerFilter, setCustomerFilter] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedAddress, setSelectedAddress] = useState<CustomerAddress | null>(null);
  const [customerDetailLoading, setCustomerDetailLoading] = useState(false);

  // ---- NS Search ----
  const [nsSearchQuery, setNsSearchQuery] = useState('');
  const [nsSearchResults, setNsSearchResults] = useState<CustomerSummary[]>([]);
  const [nsSearchLoading, setNsSearchLoading] = useState(false);

  // ---- New Customer form ----
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [newCustomerForm, setNewCustomerForm] = useState<NewCustomerForm>({
    isPerson: false,
    companyName: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });
  const [creatingCustomer, setCreatingCustomer] = useState(false);

  // ---- Create Invoice form ----
  const [invoiceCustomerId, setInvoiceCustomerId] = useState('');
  const [invoiceStartDate, setInvoiceStartDate] = useState(today());
  const [invoiceEndDate, setInvoiceEndDate] = useState(today());
  const [invoiceMemo, setInvoiceMemo] = useState('');
  const [invoiceCustomFormId, setInvoiceCustomFormId] = useState('122');
  const [invoiceDeptId, setInvoiceDeptId] = useState('5');
  const [invoiceLocationId, setInvoiceLocationId] = useState('1');
  const [invoiceTermsId, setInvoiceTermsId] = useState('4'); // Due on receipt
  const [lineItems, setLineItems] = useState<LineItemRow[]>([]);
  const [submittingInvoice, setSubmittingInvoice] = useState(false);

  // Catalog (billable items with base prices)
  const [billableItems, setBillableItems] = useState<BillableItem[]>([]);
  const [departments, setDepartments] = useState<NetSuiteDepartment[]>([]);
  const [locations, setLocations] = useState<NetSuiteLocation[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [showItemPicker, setShowItemPicker] = useState(false);
  const [itemPickerFilter, setItemPickerFilter] = useState('');

  // ---- Draft Invoices queue ----
  const [draftInvoices, setDraftInvoices] = useState<LocalInvoice[]>([]);
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmApproveId, setConfirmApproveId] = useState<string | null>(null);
  const [sendingEmailId, setSendingEmailId] = useState<string | null>(null);
  const [expandedInvoiceId, setExpandedInvoiceId] = useState<string | null>(null);

  // ---- Confirmations ----
  // These actions create invoices and send email, so the dialog is held open and spinning until
  // the action settles rather than closing on click and leaving you to guess whether it took.
  const { confirm, dialogProps: confirmDialogProps } = useConfirm();

  // ---- Running Totals ----
  const [runningTotals, setRunningTotals] = useState<ClientRunningTotal[]>([]);
  const [totalsLoading, setTotalsLoading] = useState(false);

  // ---- Invoice History ----
  const [historyInvoices, setHistoryInvoices] = useState<LocalInvoice[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyClientFilter, setHistoryClientFilter] = useState('');

  // ---- Auto-populate from activity ----
  const [showActivityPicker, setShowActivityPicker] = useState(false);
  const [activityMonth, setActivityMonth] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityResult, setActivityResult] = useState<MonthlyActivity | null>(null);

  // ---- Invoice viewer ----
  const [invoiceLookupId, setInvoiceLookupId] = useState('');
  const [viewedInvoice, setViewedInvoice] = useState<Invoice | null>(null);
  const [viewedCustomer, setViewedCustomer] = useState<Customer | null>(null);
  const [viewedAddress, setViewedAddress] = useState<CustomerAddress | null>(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  // ---- Global messages ----
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // ---- Manual auto-invoicing trigger ----
  const [runDate, setRunDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [runningAutoInvoicing, setRunningAutoInvoicing] = useState(false);

  // ---- Ref for print area ----
  const printRef = useRef<HTMLDivElement>(null);

  // --------------------------------------------------------------------------
  // Auth guard
  // --------------------------------------------------------------------------

  useEffect(() => {
    if (!isLoading && !user) {
      navigate('/login');
    } else if (!isLoading && user && !user.canAccessInvoicing) {
      navigate('/dashboard');
    }
  }, [user, isLoading, navigate]);

  useEffect(() => {
    if (isSidebarOpen && window.innerWidth < 1024) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isSidebarOpen]);

  // --------------------------------------------------------------------------
  // On mount
  // --------------------------------------------------------------------------

  useEffect(() => {
    if (!user?.canAccessInvoicing) return;

    // Health check
    checkHealth()
      .then(() => setNetsuiteStatus('connected'))
      .catch(() => setNetsuiteStatus('unreachable'));

    // Governance limits
    getGovernanceLimits()
      .then(setGovernanceLimits)
      .catch(() => { /* non-critical */ });

    // All customers for dropdown
    setCustomersLoading(true);
    getAllCustomers()
      .then(setAllCustomers)
      .catch(() => { /* non-critical */ })
      .finally(() => setCustomersLoading(false));

    // Catalog — combined billable items with base prices
    setCatalogLoading(true);
    getBillableItems()
      .then(setBillableItems)
      .catch(() => { /* non-critical */ })
      .finally(() => setCatalogLoading(false));

    // Departments and locations for the invoice form dropdowns
    getDepartments()
      .then(setDepartments)
      .catch(() => { /* non-critical */ });
    getLocations()
      .then(setLocations)
      .catch(() => { /* non-critical */ });
  }, [user?.canAccessInvoicing]);

  // --------------------------------------------------------------------------
  // Tab activation effects
  // --------------------------------------------------------------------------

  const loadDrafts = useCallback(async () => {
    setDraftsLoading(true);
    try {
      const results = await listInvoices('Draft');
      setDraftInvoices(results);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setDraftsLoading(false);
    }
  }, []);

  // Pre-load draft count on mount so the badge shows before the tab is clicked
  useEffect(() => {
    if (user?.canAccessInvoicing) loadDrafts();
  }, [user?.canAccessInvoicing, loadDrafts]);

  const doRunAutoInvoicing = useCallback(async () => {
    setError('');
    setSuccess('');
    setRunningAutoInvoicing(true);
    try {
      const result = await triggerAutoInvoicing(runDate);
      setSuccess(
        `Run complete for ${result.date}: ${result.draftsCreated} draft(s) created, ` +
        `${result.skippedAlreadyExists} skipped (already exists), ${result.skippedNoActivity} skipped (no activity), ` +
        `${result.errors} error(s).`
      );
      setTimeout(() => setSuccess(''), 8000);
      await loadDrafts();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setRunningAutoInvoicing(false);
    }
  }, [runDate, loadDrafts]);

  const handleRunAutoInvoicing = useCallback(() => {
    confirm({
      title: 'Run auto-invoicing?',
      message: (
        <>
          <p>
            This creates draft invoices for every eligible client with billable activity in the
            period starting <span className="font-semibold text-[#0f172a]">{runDate}</span>.
            Clients that already have a draft for it are skipped.
          </p>
          <p className="mt-2">
            Nothing reaches NetSuite yet — the drafts still have to be approved.
          </p>
        </>
      ),
      confirmLabel: 'Run auto-invoicing',
      action: doRunAutoInvoicing,
    });
  }, [runDate, doRunAutoInvoicing, confirm]);

  const loadRunningTotals = useCallback(async () => {
    setTotalsLoading(true);
    try {
      const results = await getRunningTotals();
      setRunningTotals(results);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setTotalsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'pending' && user?.canAccessInvoicing) {
      loadDrafts();
    }
  }, [activeTab, user?.canAccessInvoicing, loadDrafts]);

  useEffect(() => {
    if (activeTab === 'totals' && user?.canAccessInvoicing) {
      loadRunningTotals();
    }
  }, [activeTab, user?.canAccessInvoicing, loadRunningTotals]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const all = await listInvoices();
      setHistoryInvoices(all);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'history' && user?.canAccessInvoicing) {
      loadHistory();
    }
  }, [activeTab, user?.canAccessInvoicing, loadHistory]);

  const handleFillFromActivity = async () => {
    if (!selectedCustomerId || !activityMonth) return;
    setActivityLoading(true); setActivityResult(null);
    try {
      const data = await getMonthlyActivity(selectedCustomerId, activityMonth);
      setActivityResult(data);
      if (data.breakdown.length > 0) {
        // Map activity serviceType strings to catalog category values.
        // Non-MVR/NMVTIS services are stored as category "Service" in the catalog.
        const toCatalogCategory = (serviceType: string): string => {
          const map: Record<string, string> = {
            'VIN Decode': 'Service',
            'NVS': 'Service',
            'Idaho P2P': 'Service',
          };
          return map[serviceType] ?? serviceType;
        };

        const skipped: string[] = [];
        const newItems: LineItemRow[] = data.breakdown.flatMap((b) => {
          const catalogCategory = toCatalogCategory(b.serviceType);
          const catalogItem = billableItems.find((item) => {
            if (!item.id) return false;
            const sameCategory = item.category?.toLowerCase() === catalogCategory.toLowerCase();
            if (b.serviceType === 'MVR' && b.stateCode) {
              return sameCategory && item.stateCode?.toUpperCase() === b.stateCode.toUpperCase();
            }
            return sameCategory;
          });
          if (!catalogItem?.id) {
            const label = b.stateCode ? `${b.serviceType} (${b.stateCode})` : b.serviceType;
            skipped.push(label);
            return [];
          }
          const label = b.stateCode ? `${b.serviceType} (${b.stateCode})` : b.serviceType;
          return [{
            itemId: catalogItem.id,
            description: `${label} — ${data.month}`,
            // The rate actually charged, straight from the activity row. Deriving it as
            // totalCost/count blends a tiered service's bands into a rate nobody was billed at,
            // and the blend doesn't multiply back: 4,999 at 1.35 plus 1 at 1.10 is 6,749.75,
            // while 5,000 at the 1.35 average is 6,750.00. Activity now returns one row per
            // rate, so each line multiplies out exactly.
            rate: b.unitPrice,
            quantity: b.count,
            priceSource: 'aas' as const,
          }];
        });

        if (skipped.length > 0) {
          setError(`Could not match these service types to a NetSuite catalog item — add them manually: ${skipped.join(', ')}`);
        }
        setLineItems((prev) => [...prev, ...newItems]);
        setInvoiceStartDate(data.periodStart);
        setInvoiceEndDate(data.periodEnd);
        setShowActivityPicker(false);
        setActivityResult(null);
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setActivityLoading(false);
    }
  };

  // --------------------------------------------------------------------------
  // Customer selection
  // --------------------------------------------------------------------------

  const filteredCustomers = allCustomers.filter((c) => {
    const q = customerFilter.toLowerCase();
    return (
      (c.companyName || '').toLowerCase().includes(q) ||
      (c.entityId || '').toLowerCase().includes(q) ||
      (c.id || '').includes(q)
    );
  });

  const handleSelectCustomer = useCallback(async (id: string) => {
    setSelectedCustomerId(id);
    setDropdownOpen(false);
    setInvoiceCustomerId(id);
    setCustomerDetailLoading(true);
    setSelectedAddress(null);
    try {
      const detail = await getCustomer(id);
      setSelectedCustomer(detail);
      getCustomerAddress(id).then(setSelectedAddress).catch(() => setSelectedAddress(null));
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setCustomerDetailLoading(false);
    }
  }, []);

  const handleNsSearch = async () => {
    if (!nsSearchQuery.trim()) return;
    setNsSearchLoading(true);
    setNsSearchResults([]);
    try {
      const results = await searchCustomers(nsSearchQuery.trim());
      setNsSearchResults(results);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setNsSearchLoading(false);
    }
  };

  const handleCreateCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreatingCustomer(true);
    setError('');
    try {
      const req: CreateCustomerRequest = {
        isPerson: newCustomerForm.isPerson,
        companyName: newCustomerForm.isPerson ? undefined : newCustomerForm.companyName || undefined,
        firstName: newCustomerForm.isPerson ? newCustomerForm.firstName || undefined : undefined,
        lastName: newCustomerForm.isPerson ? newCustomerForm.lastName || undefined : undefined,
        email: newCustomerForm.email || undefined,
        phone: newCustomerForm.phone || undefined,
      };
      const created = await createCustomer(req);
      setSuccess('Customer created successfully.');
      setShowNewCustomer(false);
      setNewCustomerForm({ isPerson: false, companyName: '', firstName: '', lastName: '', email: '', phone: '' });
      if (created.id) {
        const summary: CustomerSummary = { id: created.id, companyName: created.companyName, entityId: created.entityId };
        setAllCustomers((prev) => [summary, ...prev]);
        await handleSelectCustomer(created.id);
      }
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setCreatingCustomer(false);
    }
  };

  // --------------------------------------------------------------------------
  // Line items
  // --------------------------------------------------------------------------

  const allPickableItems: Array<{ id: string; label: string; price: number; stateCode?: string; source: string }> =
    billableItems
      .map((b) => ({
        id: b.id || '',
        label: b.name || b.id || '',
        price: b.price || 0,
        stateCode: b.stateCode,
        source: b.category || 'Item',
      }))
      .filter((i) => i.id);

  const filteredPickableItems = allPickableItems.filter((i) => {
    const q = itemPickerFilter.toLowerCase();
    return i.label.toLowerCase().includes(q) || i.source.toLowerCase().includes(q);
  });

  const handleAddItem = async (item: { id: string; label: string; price: number; stateCode?: string; source: string }) => {
    setShowItemPicker(false);
    setItemPickerFilter('');

    let rate = item.price;
    let priceSource: LineItemRow['priceSource'] = 'catalog';

    if (selectedCustomerId) {
      try {
        const serviceType = item.source === 'MVR' ? 'MVR'
          : item.source === 'NMVTIS' ? 'NMVTIS'
          : 'VINDecode';
        const aasPrice = await getAasPriceForNsCustomer(selectedCustomerId, serviceType, item.stateCode);
        if (aasPrice !== null) {
          rate = aasPrice;
          priceSource = 'aas';
        }
      } catch (err) {
        console.warn('AAS price lookup failed, using catalog price:', err);
      }
    }

    setLineItems((prev) => {
      if (prev.some((r) => r.itemId === item.id)) {
        setError(`"${item.label}" is already in the line items.`);
        return prev;
      }
      return [...prev, { itemId: item.id, description: item.label, rate, quantity: 1, priceSource }];
    });
  };

  const handleRemoveLine = (index: number) => {
    setLineItems((prev) => prev.filter((_, i) => i !== index));
  };

  const updateLine = <K extends keyof LineItemRow>(index: number, field: K, value: LineItemRow[K]) => {
    setLineItems((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const invoiceTotal = lineItems.reduce((sum, r) => sum + r.rate * r.quantity, 0);

  // --------------------------------------------------------------------------
  // Create Draft Invoice (local DB, not direct-to-NetSuite)
  // --------------------------------------------------------------------------

  const handleCreateInvoice = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!invoiceStartDate || !invoiceEndDate) {
      setError('Start date and end date are required.');
      return;
    }
    if (lineItems.length === 0) {
      setError('At least one line item is required.');
      return;
    }

    const nsCustomerId = invoiceCustomerId || selectedCustomerId || undefined;

    const req = {
      netSuiteCustomerId: nsCustomerId || undefined,
      periodStart: invoiceStartDate,
      periodEnd: invoiceEndDate,
      totalAmount: invoiceTotal,
      customFormId: invoiceCustomFormId || undefined,
      departmentId: invoiceDeptId || undefined,
      locationId: invoiceLocationId || undefined,
      termsId: invoiceTermsId || undefined,
      startDate: invoiceStartDate,
      endDate: invoiceEndDate,
      memo: invoiceMemo || undefined,
      lines: lineItems.map((row) => ({
        itemId: row.itemId,
        description: row.description || undefined,
        rate: row.rate,
        quantity: row.quantity,
      })),
    };

    setSubmittingInvoice(true);
    try {
      await createDraftInvoice(req);
      setSuccess('Draft invoice saved. It is now in the Pending Approval queue.');
      setLineItems([]);
      setInvoiceMemo('');
      setTimeout(() => setSuccess(''), 6000);
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setSubmittingInvoice(false);
    }
  };

  // --------------------------------------------------------------------------
  // Draft queue actions
  // --------------------------------------------------------------------------

  // A ref, not state: state is only readable after the next render, so a second click that lands
  // before it would still start a second request. Approve takes 15-20 seconds (two NetSuite round
  // trips) and every extra request is another chance at a duplicate invoice in NetSuite.
  const approveInFlight = useRef(false);

  const handleApprove = async (id: string) => {
    if (approveInFlight.current) return;
    approveInFlight.current = true;
    setApprovingId(id);
    try {
      await approveInvoice(id);
      setSuccess('Invoice approved and sent to NetSuite.');
      setDraftInvoices((prev) => prev.filter((inv) => inv.id !== id));
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      approveInFlight.current = false;
      setApprovingId(null);
      setConfirmApproveId(null);
    }
  };

  const handleDelete = async (id: string) => {
    setConfirmDeleteId(null);
    setDeletingId(id);
    try {
      await deleteInvoice(id);
      setDraftInvoices((prev) => prev.filter((inv) => inv.id !== id));
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setDeletingId(null);
    }
  };

  const doSendEmail = async (inv: LocalInvoice) => {
    setSendingEmailId(inv.id);
    try {
      const result = await sendInvoiceEmail(inv.id);
      setSuccess(`Invoice emailed to ${result.recipientCount} recipient${result.recipientCount !== 1 ? 's' : ''}.`);
      // Reflect the send in the history table without a full reload.
      setHistoryInvoices((prev) => prev.map((row) =>
        row.id === inv.id
          ? {
              ...row,
              lastEmailedAt: result.lastEmailedAt,
              emailSendCount: result.emailSendCount,
              lastEmailRecipientCount: result.recipientCount,
            }
          : row
      ));
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSendingEmailId(null);
    }
  };

  const handleSendEmail = (inv: LocalInvoice) => {
    // Guard against re-sending the same invoice repeatedly in one day.
    if (isToday(inv.lastEmailedAt)) {
      const recipients = inv.lastEmailRecipientCount;
      confirm({
        title: 'Send this invoice again?',
        message: (
          <>
            It already went out today at{' '}
            <span className="font-semibold text-[#0f172a]">{fmtDateTime(inv.lastEmailedAt)}</span> to{' '}
            <span className="font-semibold text-[#0f172a]">
              {recipients} recipient{recipients !== 1 ? 's' : ''}
            </span>. Sending again delivers a second copy to the same people.
          </>
        ),
        confirmLabel: 'Send again',
        tone: 'danger',
        action: () => doSendEmail(inv),
      });
      return;
    }

    return doSendEmail(inv);
  };

  // --------------------------------------------------------------------------
  // Invoice viewer (NetSuite)
  // --------------------------------------------------------------------------

  const loadInvoice = useCallback(async (id: string) => {
    if (!id.trim()) return;
    setInvoiceLoading(true);
    setViewedInvoice(null);
    setViewedCustomer(null);
    setViewedAddress(null);
    try {
      const inv = await getInvoice(id.trim());
      setViewedInvoice(inv);
      const customerId = inv.entity?.id;
      if (customerId) {
        try {
          const [cust, addr] = await Promise.all([
            getCustomer(customerId),
            getCustomerAddress(customerId).catch(() => null),
          ]);
          setViewedCustomer(cust);
          setViewedAddress(addr);
        } catch {
          /* skip */
        }
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setInvoiceLoading(false);
    }
  }, []);

  const handleInvoiceLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    setViewedInvoice(null);
    setViewedCustomer(null);
    setViewedAddress(null);
    await loadInvoice(invoiceLookupId);
  };

  const handlePrint = () => {
    window.print();
  };

  // --------------------------------------------------------------------------
  // Loading / guard renders
  // --------------------------------------------------------------------------

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#c41e3a] animate-spin" />
      </div>
    );
  }

  if (!user) return null;

  // --------------------------------------------------------------------------
  // Render helpers
  // --------------------------------------------------------------------------

  const billToName =
    viewedCustomer?.companyName ||
    viewedCustomer?.entityId ||
    (viewedCustomer?.firstName && viewedCustomer?.lastName
      ? `${viewedCustomer.firstName} ${viewedCustomer.lastName}`
      : undefined) ||
    viewedInvoice?.entity?.refName ||
    '';

  const tabs: { id: ActiveTab; label: string; icon: React.ReactNode }[] = [
    { id: 'create', label: 'Create Invoice', icon: <FileText className="w-4 h-4" /> },
    { id: 'pending', label: 'Pending Approval', icon: <Clock className="w-4 h-4" /> },
    { id: 'history', label: 'Invoice History', icon: <Receipt className="w-4 h-4" /> },
    { id: 'totals', label: 'Running Totals', icon: <BarChart3 className="w-4 h-4" /> },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0] text-[#0f172a]">
      {/* Ambient background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none print:hidden">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-radial from-[#c41e3a]/[0.03] to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-radial from-[#c41e3a]/[0.04] to-transparent rounded-full blur-3xl" />
      </div>

      <Navigation onMenuClick={() => setIsSidebarOpen(true)} />
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      {/* Main content */}
      <div className="pt-28 md:pt-32 lg:ml-[220px] flex-1 print:pt-0 print:ml-0">

        {/* ------------------------------------------------------------------ */}
        {/* Page header                                                          */}
        {/* ------------------------------------------------------------------ */}
        <div className="relative z-20 px-4 md:px-8 py-6 print:hidden">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#c41e3a]/10">
                <Receipt className="w-5 h-5 text-[#c41e3a]" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-[#0f172a]">Invoicing</h1>
                <p className="text-[#64748b] mt-0.5">Create and manage invoices through the approval queue</p>
              </div>
            </div>
          </div>
        </div>

        <div className="relative z-10 px-4 md:px-8 pb-12 print:px-0 print:pb-0">
          <div className="max-w-6xl mx-auto space-y-6">

            {/* Global messages */}
            {error && (
              <div className="p-4 rounded-xl border flex items-start space-x-3 print:hidden"
                style={{ backgroundColor: 'rgba(220, 38, 38, 0.1)', borderColor: 'rgba(220, 38, 38, 0.3)' }}>
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800 flex-1">{error}</p>
                <button onClick={() => setError('')}><X className="w-4 h-4 text-red-600" /></button>
              </div>
            )}
            {success && (
              <div className="p-4 rounded-xl border flex items-center space-x-3 print:hidden"
                style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', borderColor: 'rgba(34, 197, 94, 0.3)' }}>
                <CheckCircle className="w-5 h-5 text-green-600" />
                <p className="text-sm text-green-800">{success}</p>
              </div>
            )}

            {/* ================================================================ */}
            {/* A) NETSUITE STATUS STRIP                                          */}
            {/* ================================================================ */}
            <div className="glass-card p-4 flex flex-wrap items-center gap-4 print:hidden">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-[#475569]">NetSuite:</span>
                {netsuiteStatus === 'checking' && (
                  <span className="flex items-center space-x-1 text-sm text-[#94a3b8]">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Checking...</span>
                  </span>
                )}
                {netsuiteStatus === 'connected' && (
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-500/15 text-green-700">
                    Connected
                  </span>
                )}
                {netsuiteStatus === 'unreachable' && (
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-500/15 text-red-700">
                    Unreachable
                  </span>
                )}
              </div>
              {governanceLimits && (() => {
                const g = governanceLimits;
                const remaining = g.remainingUsage ?? g.remaining;
                let label = '';
                let value = '';
                if (typeof remaining === 'number') {
                  label = 'API requests remaining';
                  value = remaining.toLocaleString();
                } else if (g.accountConcurrencyLimit != null) {
                  label = 'API concurrency';
                  const free = g.accountUnallocatedConcurrencyLimit ?? g.accountConcurrencyLimit;
                  value = `${free} of ${g.accountConcurrencyLimit} available`;
                }
                if (!value) return null;
                return (
                  <div className="flex items-center space-x-2 text-sm text-[#64748b]">
                    <span>{label}:</span>
                    <span className="font-semibold text-[#0f172a]">{value}</span>
                  </div>
                );
              })()}
            </div>

            {/* ================================================================ */}
            {/* B) TAB BAR                                                        */}
            {/* ================================================================ */}
            <div className="glass-card p-1.5 flex space-x-1 print:hidden">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    activeTab === tab.id
                      ? 'bg-[#c41e3a] text-white shadow-md shadow-red-500/20'
                      : 'text-[#64748b] hover:text-[#0f172a] hover:bg-black/[0.04]'
                  }`}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                  {tab.id === 'pending' && draftInvoices.length > 0 && (
                    <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs font-semibold leading-none ${
                      activeTab === 'pending' ? 'bg-white/30 text-white' : 'bg-[#c41e3a]/10 text-[#c41e3a]'
                    }`}>
                      {draftInvoices.length}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* ================================================================ */}
            {/* C) CREATE TAB                                                     */}
            {/* ================================================================ */}
            {activeTab === 'create' && (
              <>
                {/* Customer section */}
                <div className="glass-card p-6 print:hidden">
                  <div className="flex items-center space-x-3 mb-5">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-blue-500/10">
                      <Building2 className="w-5 h-5 text-blue-600" />
                    </div>
                    <h2 className="text-lg font-semibold text-[#0f172a]">Customer</h2>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Left: dropdown + NS search */}
                    <div className="space-y-4">
                      {/* Searchable dropdown from all customers */}
                      <div>
                        <label className="block text-sm font-medium text-[#475569] mb-1.5">
                          Select Customer
                        </label>
                        <div className="relative">
                          <div
                            className="flex items-center w-full px-3 py-2.5 rounded-xl border border-black/[0.10] bg-white/80 cursor-pointer"
                            onClick={() => setDropdownOpen((o) => !o)}
                          >
                            <span className="flex-1 text-sm text-[#0f172a] truncate">
                              {selectedCustomer
                                ? (selectedCustomer.companyName || selectedCustomer.entityId || selectedCustomerId)
                                : customersLoading ? 'Loading...' : 'Choose a customer...'}
                            </span>
                            <ChevronDown className="w-4 h-4 text-[#94a3b8] flex-shrink-0" />
                          </div>
                          {dropdownOpen && (
                            <div className="absolute z-50 mt-1 w-full bg-white/95 backdrop-blur-xl rounded-xl border border-black/[0.08] shadow-xl max-h-64 flex flex-col">
                              <div className="p-2 border-b border-black/[0.06]">
                                <input
                                  autoFocus
                                  type="text"
                                  value={customerFilter}
                                  onChange={(e) => setCustomerFilter(e.target.value)}
                                  placeholder="Filter by name or ID..."
                                  className="w-full px-3 py-2 text-sm rounded-lg border border-black/[0.08] bg-white/80 focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30"
                                />
                              </div>
                              <div className="overflow-y-auto flex-1">
                                {filteredCustomers.length === 0 ? (
                                  <p className="px-3 py-4 text-sm text-[#94a3b8] text-center">No customers found</p>
                                ) : filteredCustomers.map((c) => (
                                  <button
                                    key={c.id}
                                    onClick={() => { setCustomerFilter(''); handleSelectCustomer(c.id || ''); }}
                                    className="w-full text-left px-3 py-2.5 text-sm hover:bg-[#c41e3a]/5 transition-colors flex items-center justify-between"
                                  >
                                    <span className="font-medium text-[#0f172a] truncate">
                                      {c.companyName || c.entityId || c.id}
                                    </span>
                                    <span className="text-[#94a3b8] text-xs ml-2 flex-shrink-0">#{c.id}</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Live NetSuite search */}
                      <div>
                        <label className="block text-sm font-medium text-[#475569] mb-1.5">
                          Search NetSuite
                        </label>
                        <div className="flex space-x-2">
                          <input
                            type="text"
                            value={nsSearchQuery}
                            onChange={(e) => setNsSearchQuery(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleNsSearch(); } }}
                            placeholder="Search by company name..."
                            className="flex-1 px-3 py-2.5 text-sm rounded-xl border border-black/[0.10] bg-white/80 focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30"
                          />
                          <button
                            onClick={handleNsSearch}
                            disabled={nsSearchLoading}
                            className="px-4 py-2.5 rounded-xl bg-[#c41e3a] hover:bg-[#a51830] text-white text-sm font-medium disabled:opacity-50 flex items-center space-x-1"
                          >
                            {nsSearchLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                          </button>
                        </div>
                        {nsSearchResults.length > 0 && (
                          <div className="mt-2 rounded-xl border border-black/[0.08] bg-white/90 overflow-hidden">
                            {nsSearchResults.map((r) => (
                              <button
                                key={r.id}
                                onClick={() => { setNsSearchResults([]); setNsSearchQuery(''); handleSelectCustomer(r.id || ''); }}
                                className="w-full text-left px-3 py-2.5 text-sm hover:bg-[#c41e3a]/5 transition-colors flex items-center justify-between border-b border-black/[0.04] last:border-b-0"
                              >
                                <span className="font-medium text-[#0f172a]">{r.companyName || r.entityId || r.id}</span>
                                <span className="text-[#94a3b8] text-xs">#{r.id}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* New Customer toggle */}
                      <button
                        onClick={() => setShowNewCustomer((v) => !v)}
                        className="flex items-center space-x-2 text-sm text-[#c41e3a] hover:text-[#a51830] font-medium"
                      >
                        <Plus className="w-4 h-4" />
                        <span>{showNewCustomer ? 'Cancel new customer' : 'Create new customer'}</span>
                      </button>

                      {showNewCustomer && (
                        <form onSubmit={handleCreateCustomer} className="p-4 rounded-xl border border-black/[0.08] bg-white/50 space-y-3">
                          <h3 className="font-medium text-[#0f172a] flex items-center space-x-2">
                            <User className="w-4 h-4" />
                            <span>New Customer</span>
                          </h3>

                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={newCustomerForm.isPerson}
                              onChange={(e) => setNewCustomerForm({ ...newCustomerForm, isPerson: e.target.checked })}
                              className="rounded"
                            />
                            <span className="text-sm text-[#475569]">Individual (person)</span>
                          </label>

                          {newCustomerForm.isPerson ? (
                            <div className="grid grid-cols-2 gap-2">
                              <input type="text" placeholder="First name" value={newCustomerForm.firstName}
                                onChange={(e) => setNewCustomerForm({ ...newCustomerForm, firstName: e.target.value })}
                                className="px-3 py-2 text-sm rounded-lg border border-black/[0.08] bg-white/80 focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30" />
                              <input type="text" placeholder="Last name" value={newCustomerForm.lastName}
                                onChange={(e) => setNewCustomerForm({ ...newCustomerForm, lastName: e.target.value })}
                                className="px-3 py-2 text-sm rounded-lg border border-black/[0.08] bg-white/80 focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30" />
                            </div>
                          ) : (
                            <input type="text" placeholder="Company name" value={newCustomerForm.companyName}
                              onChange={(e) => setNewCustomerForm({ ...newCustomerForm, companyName: e.target.value })}
                              className="w-full px-3 py-2 text-sm rounded-lg border border-black/[0.08] bg-white/80 focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30" />
                          )}

                          <input type="email" placeholder="Email (optional)" value={newCustomerForm.email}
                            onChange={(e) => setNewCustomerForm({ ...newCustomerForm, email: e.target.value })}
                            className="w-full px-3 py-2 text-sm rounded-lg border border-black/[0.08] bg-white/80 focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30" />

                          <input type="tel" placeholder="(555) 555-0100 (optional)" value={newCustomerForm.phone}
                            onChange={(e) => setNewCustomerForm({ ...newCustomerForm, phone: maskPhoneInput(e.target.value) })}
                            className="w-full px-3 py-2 text-sm rounded-lg border border-black/[0.08] bg-white/80 focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30" />

                          <button type="submit" disabled={creatingCustomer}
                            className="w-full py-2 rounded-xl bg-[#c41e3a] hover:bg-[#a51830] text-white text-sm font-medium disabled:opacity-50 flex items-center justify-center space-x-2">
                            {creatingCustomer ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                            <span>{creatingCustomer ? 'Creating...' : 'Create Customer'}</span>
                          </button>
                        </form>
                      )}
                    </div>

                    {/* Right: selected customer detail */}
                    <div>
                      <label className="block text-sm font-medium text-[#475569] mb-1.5">
                        Selected Customer
                      </label>
                      {customerDetailLoading && (
                        <div className="flex items-center space-x-2 text-[#94a3b8] text-sm py-4">
                          <Loader2 className="w-4 h-4 animate-spin" /> <span>Loading...</span>
                        </div>
                      )}
                      {!customerDetailLoading && selectedCustomer && (
                        <div className="rounded-xl border border-black/[0.08] bg-white/50 overflow-hidden">
                          <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-black/[0.06] bg-black/[0.02]">
                            <span className="font-semibold text-[#0f172a] truncate">
                              {selectedCustomer.companyName ||
                                `${selectedCustomer.firstName || ''} ${selectedCustomer.lastName || ''}`.trim() ||
                                selectedCustomer.entityId || selectedCustomerId}
                            </span>
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ${
                              selectedCustomer.isInactive ? 'bg-red-500/15 text-red-700' : 'bg-green-500/15 text-green-700'
                            }`}>
                              {selectedCustomer.isInactive ? 'Inactive' : 'Active'}
                            </span>
                          </div>
                          <dl className="divide-y divide-black/[0.04]">
                            {[
                              { label: 'Entity ID', value: selectedCustomer.entityId },
                              { label: 'Type', value: selectedCustomer.isPerson ? 'Person' : 'Company' },
                              { label: 'Email', value: selectedCustomer.email },
                              { label: 'Phone', value: formatPhone(selectedCustomer.phone) },
                              { label: 'NetSuite ID', value: selectedCustomer.id },
                            ].map((row) => (
                              <div key={row.label} className="flex items-start justify-between gap-3 px-4 py-2">
                                <dt className="text-xs font-medium text-[#94a3b8] flex-shrink-0 pt-0.5">{row.label}</dt>
                                <dd className="text-sm text-[#0f172a] text-right break-words">{row.value || '—'}</dd>
                              </div>
                            ))}
                          </dl>
                          {selectedAddress && (
                            <div className="px-4 py-3 border-t border-black/[0.06]">
                              <div className="text-xs font-medium text-[#94a3b8] mb-1">Billing Address</div>
                              <div className="text-sm text-[#0f172a] leading-snug">
                                {selectedAddress.addressee && <div>{selectedAddress.addressee}</div>}
                                {selectedAddress.addr1 && <div>{selectedAddress.addr1}</div>}
                                {selectedAddress.addr2 && <div>{selectedAddress.addr2}</div>}
                                <div>
                                  {[selectedAddress.city, selectedAddress.state, selectedAddress.zip]
                                    .filter(Boolean).join(', ')}
                                </div>
                                {selectedAddress.country && <div>{selectedAddress.country}</div>}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      {!customerDetailLoading && !selectedCustomer && (
                        <div className="p-4 rounded-xl border border-dashed border-black/[0.10] text-sm text-[#94a3b8] text-center">
                          No customer selected
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Create Invoice form */}
                <div className="glass-card p-6 print:hidden">
                  <div className="flex items-center space-x-3 mb-5">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#c41e3a]/10">
                      <FileText className="w-5 h-5 text-[#c41e3a]" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-[#0f172a]">Create Invoice</h2>
                      <p className="text-xs text-[#94a3b8] mt-0.5">Saved as a Draft — approve it from the Pending Approval tab to send to NetSuite</p>
                    </div>
                  </div>

                  <form onSubmit={handleCreateInvoice} className="space-y-5">
                    {/* Header fields */}
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-[#475569] mb-1.5">
                          Customer ID <span className="text-[#94a3b8] font-normal">(NetSuite)</span>
                        </label>
                        <input
                          type="text"
                          value={invoiceCustomerId}
                          onChange={(e) => setInvoiceCustomerId(e.target.value)}
                          placeholder={selectedCustomerId || '312'}
                          className="w-full px-3 py-2.5 text-sm rounded-xl border border-black/[0.10] bg-white/80 focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#475569] mb-1.5">
                          Start Date <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          required
                          value={invoiceStartDate}
                          onChange={(e) => setInvoiceStartDate(e.target.value)}
                          className="w-full px-3 py-2.5 text-sm rounded-xl border border-black/[0.10] bg-white/80 focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#475569] mb-1.5">
                          End Date <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          required
                          value={invoiceEndDate}
                          onChange={(e) => setInvoiceEndDate(e.target.value)}
                          className="w-full px-3 py-2.5 text-sm rounded-xl border border-black/[0.10] bg-white/80 focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#475569] mb-1.5">Memo</label>
                        <input
                          type="text"
                          value={invoiceMemo}
                          onChange={(e) => setInvoiceMemo(e.target.value)}
                          placeholder="Optional memo"
                          className="w-full px-3 py-2.5 text-sm rounded-xl border border-black/[0.10] bg-white/80 focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#475569] mb-1.5">
                          Custom Form ID
                        </label>
                        <input
                          type="text"
                          value={invoiceCustomFormId}
                          onChange={(e) => setInvoiceCustomFormId(e.target.value)}
                          placeholder="122"
                          className="w-full px-3 py-2.5 text-sm rounded-xl border border-black/[0.10] bg-white/80 focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#475569] mb-1.5">
                          Department
                        </label>
                        {departments.length > 0 ? (
                          <select
                            value={invoiceDeptId}
                            onChange={(e) => setInvoiceDeptId(e.target.value)}
                            className="w-full px-3 py-2.5 text-sm rounded-xl border border-black/[0.10] bg-white/80 focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30"
                          >
                            {!departments.some((d) => d.id === invoiceDeptId) && invoiceDeptId && (
                              <option value={invoiceDeptId}>Department {invoiceDeptId}</option>
                            )}
                            {departments.map((d) => (
                              <option key={d.id} value={d.id}>{d.name || d.id}</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="text"
                            value={invoiceDeptId}
                            onChange={(e) => setInvoiceDeptId(e.target.value)}
                            placeholder="5"
                            className="w-full px-3 py-2.5 text-sm rounded-xl border border-black/[0.10] bg-white/80 focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30"
                          />
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#475569] mb-1.5">
                          Location
                        </label>
                        {locations.length > 0 ? (
                          <select
                            value={invoiceLocationId}
                            onChange={(e) => setInvoiceLocationId(e.target.value)}
                            className="w-full px-3 py-2.5 text-sm rounded-xl border border-black/[0.10] bg-white/80 focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30"
                          >
                            {!locations.some((l) => l.id === invoiceLocationId) && invoiceLocationId && (
                              <option value={invoiceLocationId}>Location {invoiceLocationId}</option>
                            )}
                            {locations.map((l) => (
                              <option key={l.id} value={l.id}>{l.name || l.id}</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="text"
                            value={invoiceLocationId}
                            onChange={(e) => setInvoiceLocationId(e.target.value)}
                            placeholder="1"
                            className="w-full px-3 py-2.5 text-sm rounded-xl border border-black/[0.10] bg-white/80 focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30"
                          />
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#475569] mb-1.5">
                          Terms
                        </label>
                        <select
                          value={invoiceTermsId}
                          onChange={(e) => setInvoiceTermsId(e.target.value)}
                          className="w-full px-3 py-2.5 text-sm rounded-xl border border-black/[0.10] bg-white/80 focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30"
                        >
                          {INVOICE_TERMS.map((t) => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Line items table */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-[#0f172a]">Line Items</h3>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setShowActivityPicker((v) => !v)}
                            className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 text-sm font-medium transition-colors border border-blue-200"
                          >
                            <Wand2 className="w-4 h-4" />
                            <span>Fill from activity</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowItemPicker(true)}
                            className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg bg-[#c41e3a]/10 hover:bg-[#c41e3a]/20 text-[#c41e3a] text-sm font-medium transition-colors"
                          >
                            <Plus className="w-4 h-4" />
                            <span>Add Line Item</span>
                          </button>
                        </div>
                      </div>

                      {showActivityPicker && (
                        <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-4 space-y-3">
                          <p className="text-xs font-semibold text-blue-700">Auto-populate from billable activity</p>
                          {!selectedCustomerId && (
                            <p className="text-xs text-amber-700">Select a customer above first.</p>
                          )}
                          <div className="flex flex-wrap gap-3 items-end">
                            <div>
                              <label className="block text-xs text-[#64748b] mb-1">Month</label>
                              <input
                                type="month"
                                value={activityMonth}
                                onChange={(e) => setActivityMonth(e.target.value)}
                                className="px-3 py-1.5 rounded-lg border border-black/[0.08] bg-white text-sm focus:outline-none focus:border-blue-400"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={handleFillFromActivity}
                              disabled={!selectedCustomerId || !activityMonth || activityLoading}
                              className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-50 transition-colors"
                            >
                              {activityLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                              Fill
                            </button>
                          </div>
                          {activityResult && activityResult.breakdown.length === 0 && (
                            <p className="text-xs text-amber-700">No billable activity found for that client and month.</p>
                          )}
                        </div>
                      )}

                      {lineItems.length > 0 ? (
                        <div className="rounded-xl border border-black/[0.08] overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-black/[0.03]">
                              <tr>
                                <th className="text-left px-4 py-2.5 font-medium text-[#64748b]">Item / Description</th>
                                <th className="text-right px-4 py-2.5 font-medium text-[#64748b] w-28">Qty</th>
                                <th className="text-right px-4 py-2.5 font-medium text-[#64748b] w-32">Rate</th>
                                <th className="text-right px-4 py-2.5 font-medium text-[#64748b] w-28">Amount</th>
                                <th className="px-4 py-2.5 w-10"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {lineItems.map((row, i) => (
                                <tr key={i} className="border-t border-black/[0.06]">
                                  <td className="px-4 py-2">
                                    <input
                                      type="text"
                                      value={row.description}
                                      onChange={(e) => updateLine(i, 'description', e.target.value)}
                                      className="w-full px-2 py-1 text-sm rounded-lg border border-black/[0.08] bg-white/70 focus:outline-none focus:ring-1 focus:ring-[#c41e3a]/30"
                                    />
                                    {row.priceSource === 'aas' && (
                                      <span className="text-[10px] text-emerald-600 font-medium mt-0.5 block">client price</span>
                                    )}
                                    {row.priceSource === 'catalog' && selectedCustomerId && (
                                      <span className="text-[10px] text-amber-500 font-medium mt-0.5 block">catalog price — client not linked</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-2">
                                    <input
                                      type="number"
                                      min="1"
                                      value={row.quantity}
                                      onChange={(e) => updateLine(i, 'quantity', Math.max(1, parseInt(e.target.value) || 1))}
                                      className="w-full px-2 py-1 text-sm text-right rounded-lg border border-black/[0.08] bg-white/70 focus:outline-none focus:ring-1 focus:ring-[#c41e3a]/30"
                                    />
                                  </td>
                                  <td className="px-4 py-2">
                                    <div className="relative">
                                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[#94a3b8] text-sm pointer-events-none">$</span>
                                      <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={row.rate}
                                        onChange={(e) => updateLine(i, 'rate', parseFloat(e.target.value) || 0)}
                                        className="w-full pl-5 pr-2 py-1 text-sm text-right rounded-lg border border-black/[0.08] bg-white/70 focus:outline-none focus:ring-1 focus:ring-[#c41e3a]/30"
                                      />
                                    </div>
                                  </td>
                                  <td className="px-4 py-2 text-right font-medium text-[#0f172a]">
                                    {fmt(row.rate * row.quantity)}
                                  </td>
                                  <td className="px-4 py-2 text-center">
                                    <button type="button" onClick={() => handleRemoveLine(i)}
                                      className="text-[#94a3b8] hover:text-red-600 transition-colors">
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot className="border-t border-black/[0.08] bg-black/[0.02]">
                              <tr>
                                <td colSpan={3} className="px-4 py-2.5 text-right text-sm font-semibold text-[#475569]">
                                  Grand Total
                                </td>
                                <td className="px-4 py-2.5 text-right text-sm font-bold text-[#0f172a]">
                                  {fmt(invoiceTotal)}
                                </td>
                                <td />
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      ) : (
                        <div className="rounded-xl border border-dashed border-black/[0.10] py-10 text-center text-[#94a3b8] text-sm">
                          No line items yet. Click "Add Line Item" to pick from the catalog.
                        </div>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={submittingInvoice || lineItems.length === 0}
                      className="w-full py-3 rounded-xl bg-gradient-to-r from-[#c41e3a] to-[#e74860] hover:from-[#a51830] hover:to-[#c41e3a] text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 shadow-lg shadow-red-500/20"
                    >
                      {submittingInvoice
                        ? <><Loader2 className="w-5 h-5 animate-spin" /><span>Saving Draft...</span></>
                        : <><Receipt className="w-5 h-5" /><span>Save as Draft</span></>}
                    </button>
                  </form>
                </div>

              </>
            )}

            {/* ================================================================ */}
            {/* D) PENDING APPROVAL TAB                                           */}
            {/* ================================================================ */}
            {activeTab === 'pending' && (
              <div className="glass-card p-6">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center space-x-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-amber-500/10">
                      <Clock className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-[#0f172a]">Pending Approval</h2>
                      <p className="text-xs text-[#94a3b8] mt-0.5">Draft invoices waiting to be approved and sent to NetSuite</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {user?.isSystemAdmin && (
                      <>
                        <input
                          type="date"
                          value={runDate}
                          onChange={(e) => setRunDate(e.target.value)}
                          disabled={runningAutoInvoicing}
                          title="Date to simulate for the auto-invoicing run (e.g. the 1st or 15th of the target period)"
                          className="px-2.5 py-2 rounded-xl border border-black/[0.10] bg-white/60 text-sm text-[#475569] disabled:opacity-50"
                        />
                        <button
                          onClick={handleRunAutoInvoicing}
                          disabled={runningAutoInvoicing}
                          title="Manually trigger the auto-invoicing run for the selected date"
                          className="flex items-center space-x-1.5 px-3 py-2 rounded-xl border border-black/[0.10] bg-white/60 hover:bg-white/90 text-sm text-[#475569] transition-colors disabled:opacity-50"
                        >
                          {runningAutoInvoicing
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : <PlayCircle className="w-4 h-4" />}
                          <span>Run Auto-Invoicing Now</span>
                        </button>
                      </>
                    )}
                    <button
                      onClick={loadDrafts}
                      disabled={draftsLoading}
                      className="flex items-center space-x-1.5 px-3 py-2 rounded-xl border border-black/[0.10] bg-white/60 hover:bg-white/90 text-sm text-[#475569] transition-colors disabled:opacity-50"
                    >
                      <RefreshCw className={`w-4 h-4 ${draftsLoading ? 'animate-spin' : ''}`} />
                      <span>Refresh</span>
                    </button>
                  </div>
                </div>

                {draftsLoading && (
                  <div className="flex items-center justify-center py-16">
                    <Loader2 className="w-7 h-7 text-[#c41e3a] animate-spin" />
                  </div>
                )}

                {!draftsLoading && draftInvoices.length === 0 && (
                  <div className="rounded-xl border border-dashed border-black/[0.10] py-16 text-center">
                    <CheckSquare className="w-10 h-10 text-[#94a3b8] mx-auto mb-3" />
                    <p className="text-sm font-medium text-[#64748b]">No drafts waiting for approval</p>
                    <p className="text-xs text-[#94a3b8] mt-1">Create an invoice from the Create Invoice tab to get started.</p>
                  </div>
                )}

                {!draftsLoading && draftInvoices.length > 0 && (
                  <div className="rounded-xl border border-black/[0.08] overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-black/[0.03]">
                        <tr>
                          <th className="px-3 py-3 w-8" />
                          <th className="text-left px-4 py-3 font-medium text-[#64748b]">Client</th>
                          <th className="text-left px-4 py-3 font-medium text-[#64748b]">Period</th>
                          <th className="text-right px-4 py-3 font-medium text-[#64748b] w-32">Total</th>
                          <th className="text-left px-4 py-3 font-medium text-[#64748b] w-36">Created</th>
                          <th className="px-4 py-3 w-40 text-center font-medium text-[#64748b]">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {draftInvoices.map((inv) => (
                          <React.Fragment key={inv.id}>
                          <tr className="border-t border-black/[0.06] hover:bg-black/[0.01] transition-colors">
                            <td className="px-3 py-3">
                              <button
                                onClick={() => setExpandedInvoiceId(expandedInvoiceId === inv.id ? null : inv.id)}
                                className="p-0.5 rounded hover:bg-black/[0.06] text-[#94a3b8] hover:text-[#475569] transition-colors"
                              >
                                {expandedInvoiceId === inv.id
                                  ? <ChevronDown className="w-4 h-4" />
                                  : <ChevronRight className="w-4 h-4" />}
                              </button>
                            </td>
                            <td className="px-4 py-3">
                              <div className="font-medium text-[#0f172a] truncate max-w-[200px]">
                                {inv.clientName || inv.clientId || '—'}
                              </div>
                              {inv.netSuiteInvoiceNumber && (
                                <div className="text-xs text-[#94a3b8] mt-0.5">NS #{inv.netSuiteInvoiceNumber}</div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-[#475569]">
                              {fmtDateShort(inv.periodStart)} – {fmtDateShort(inv.periodEnd)}
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-[#0f172a]">
                              {fmt(inv.totalAmount)}
                            </td>
                            <td className="px-4 py-3 text-[#64748b] text-xs">
                              {fmtDateShort(inv.createdAt)}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-center space-x-2">
                                {/* Approve */}
                                {confirmApproveId === inv.id ? (
                                  <div className="flex items-center space-x-1">
                                    <button
                                      onClick={() => handleApprove(inv.id)}
                                      disabled={approvingId !== null}
                                      className="px-2.5 py-1 rounded-lg bg-green-600 hover:bg-green-700 text-white text-xs font-medium disabled:opacity-50 flex items-center space-x-1"
                                    >
                                      {approvingId === inv.id
                                        ? <Loader2 className="w-3 h-3 animate-spin" />
                                        : <CheckCircle className="w-3 h-3" />}
                                      <span>{approvingId === inv.id ? 'Approving… do not click again' : 'Yes'}</span>
                                    </button>
                                    <button
                                      onClick={() => setConfirmApproveId(null)}
                                      disabled={approvingId !== null}
                                      className="px-2.5 py-1 rounded-lg border border-black/[0.10] bg-white/60 hover:bg-white text-xs text-[#64748b] disabled:opacity-50"
                                    >
                                      No
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => setConfirmApproveId(inv.id)}
                                    disabled={approvingId !== null || deletingId === inv.id}
                                    className="px-3 py-1.5 rounded-lg bg-green-600/10 hover:bg-green-600/20 text-green-700 text-xs font-medium disabled:opacity-50 flex items-center space-x-1 transition-colors"
                                  >
                                    <CheckSquare className="w-3.5 h-3.5" />
                                    <span>Approve</span>
                                  </button>
                                )}

                                {/* Delete */}
                                {confirmDeleteId === inv.id ? (
                                  <div className="flex items-center space-x-1">
                                    <button
                                      onClick={() => handleDelete(inv.id)}
                                      disabled={deletingId === inv.id}
                                      className="px-2.5 py-1 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-medium disabled:opacity-50 flex items-center space-x-1"
                                    >
                                      {deletingId === inv.id
                                        ? <Loader2 className="w-3 h-3 animate-spin" />
                                        : <Trash2 className="w-3 h-3" />}
                                      <span>Yes</span>
                                    </button>
                                    <button
                                      onClick={() => setConfirmDeleteId(null)}
                                      className="px-2.5 py-1 rounded-lg border border-black/[0.10] bg-white/60 hover:bg-white text-xs text-[#64748b]"
                                    >
                                      No
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => setConfirmDeleteId(inv.id)}
                                    disabled={approvingId !== null || deletingId === inv.id}
                                    className="px-3 py-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-600 text-xs font-medium disabled:opacity-50 flex items-center space-x-1 transition-colors"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                    <span>Delete</span>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                          {expandedInvoiceId === inv.id && (
                            <tr className="border-t border-black/[0.06] bg-black/[0.02]">
                              <td colSpan={6} className="px-6 py-4">
                                <div className="text-xs font-semibold text-[#64748b] uppercase tracking-wide mb-3">Line Items</div>
                                {inv.lines && inv.lines.length > 0 ? (
                                  <table className="w-full text-sm">
                                    <thead>
                                      <tr className="text-xs text-[#94a3b8]">
                                        <th className="text-left pb-2 font-medium">Description</th>
                                        <th className="text-right pb-2 font-medium w-24">Qty</th>
                                        <th className="text-right pb-2 font-medium w-28">Rate</th>
                                        <th className="text-right pb-2 font-medium w-28">Amount</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {inv.lines.map((line, i) => (
                                        <tr key={i} className="border-t border-black/[0.04]">
                                          <td className="py-1.5 text-[#0f172a]">{line.description || line.itemId}</td>
                                          <td className="py-1.5 text-right text-[#475569]">{line.quantity}</td>
                                          <td className="py-1.5 text-right text-[#475569]">{fmt(line.rate)}</td>
                                          <td className="py-1.5 text-right font-medium text-[#0f172a]">{fmt(line.rate * line.quantity)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                    <tfoot>
                                      <tr className="border-t border-black/[0.08]">
                                        <td colSpan={3} className="pt-2 text-right text-xs font-semibold text-[#64748b] uppercase tracking-wide">Total</td>
                                        <td className="pt-2 text-right font-bold text-[#0f172a]">{fmt(inv.totalAmount)}</td>
                                      </tr>
                                    </tfoot>
                                  </table>
                                ) : (
                                  <p className="text-sm text-[#94a3b8]">No line items recorded.</p>
                                )}
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* ================================================================ */}
            {/* E) INVOICE HISTORY TAB                                            */}
            {/* ================================================================ */}
            {activeTab === 'history' && (
              <div className="space-y-4">
                <div className="glass-card p-4 flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#c41e3a]/10">
                      <Receipt className="w-5 h-5 text-[#c41e3a]" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-[#0f172a]">Invoice History</h2>
                      <p className="text-xs text-[#94a3b8] mt-0.5">All invoices across all clients</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Filter className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#94a3b8]" />
                      <input
                        type="text"
                        placeholder="Filter by client…"
                        value={historyClientFilter}
                        onChange={(e) => setHistoryClientFilter(e.target.value)}
                        className="pl-8 pr-3 py-1.5 rounded-lg border border-black/[0.08] bg-white/80 text-sm focus:outline-none focus:border-[#c41e3a]/40 w-48"
                      />
                    </div>
                    <button onClick={loadHistory} disabled={historyLoading}
                      className="flex items-center space-x-1.5 px-3 py-2 rounded-xl border border-black/[0.10] bg-white/60 hover:bg-white/90 text-sm text-[#475569] transition-colors disabled:opacity-50">
                      <RefreshCw className={`w-4 h-4 ${historyLoading ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                </div>

                {historyLoading ? (
                  <div className="flex items-center justify-center py-16"><Loader2 className="w-7 h-7 text-[#c41e3a] animate-spin" /></div>
                ) : (() => {
                  const q = historyClientFilter.toLowerCase();
                  const filtered = historyInvoices.filter((i) =>
                    !q || (i.clientName || '').toLowerCase().includes(q)
                  );
                  return filtered.length === 0 ? (
                    <div className="glass-card p-8 text-center text-[#94a3b8] text-sm">No invoices found.</div>
                  ) : (
                    <div className="glass-card overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-black/[0.03] border-b border-black/[0.06]">
                          <tr>
                            <th className="text-left px-4 py-3 font-medium text-[#64748b]">Client</th>
                            <th className="text-left px-4 py-3 font-medium text-[#64748b]">Period</th>
                            <th className="text-left px-4 py-3 font-medium text-[#64748b]">NS Invoice #</th>
                            <th className="text-left px-4 py-3 font-medium text-[#64748b]">Status</th>
                            <th className="text-right px-4 py-3 font-medium text-[#64748b]">Amount</th>
                            <th className="text-left px-4 py-3 font-medium text-[#64748b]">Created</th>
                            <th className="text-left px-4 py-3 font-medium text-[#64748b]">Emailed</th>
                            <th className="px-4 py-3 w-32 text-center font-medium text-[#64748b]">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filtered.map((inv) => (
                            <tr key={inv.id} className="border-b border-black/[0.04] hover:bg-black/[0.01]">
                              <td className="px-4 py-3 font-medium text-[#0f172a]">{inv.clientName || '—'}</td>
                              <td className="px-4 py-3 text-[#64748b] whitespace-nowrap">
                                {fmtDateShort(inv.periodStart)} – {fmtDateShort(inv.periodEnd)}
                              </td>
                              <td className="px-4 py-3">
                                {inv.netSuiteInvoiceNumber && inv.netSuiteInvoiceId ? (
                                  <button
                                    onClick={() => {
                                      setViewedInvoice(null);
                                      setViewedCustomer(null);
                                      setViewedAddress(null);
                                      loadInvoice(inv.netSuiteInvoiceId!);
                                    }}
                                    className="text-[#c41e3a] hover:underline font-medium"
                                  >
                                    {inv.netSuiteInvoiceNumber}
                                  </button>
                                ) : (
                                  <span className="text-[#64748b]">{inv.netSuiteInvoiceNumber || '—'}</span>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                  inv.status === 'Sent' ? 'bg-green-100 text-green-700' :
                                  inv.status === 'Voided' ? 'bg-red-100 text-red-700' :
                                  'bg-amber-100 text-amber-700'
                                }`}>{inv.status}</span>
                              </td>
                              <td className="px-4 py-3 text-right font-semibold text-[#0f172a]">{fmt(inv.totalAmount)}</td>
                              <td className="px-4 py-3 text-[#94a3b8] whitespace-nowrap">{fmtDateShort(inv.createdAt)}</td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {inv.lastEmailedAt ? (
                                  <span
                                    title={`Last emailed ${fmtDateTime(inv.lastEmailedAt)} to ${inv.lastEmailRecipientCount} recipient${inv.lastEmailRecipientCount !== 1 ? 's' : ''}`}
                                    className="inline-flex items-center gap-1.5 text-[#15803d]"
                                  >
                                    <Mail className="w-3.5 h-3.5" />
                                    <span>{fmtDateShort(inv.lastEmailedAt)}</span>
                                    {inv.emailSendCount > 1 && (
                                      <span className="px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 text-[11px] font-medium">
                                        ×{inv.emailSendCount}
                                      </span>
                                    )}
                                  </span>
                                ) : (
                                  <span className="text-[#94a3b8]">Not emailed</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center">
                                {inv.status === 'Sent' && inv.netSuiteInvoiceNumber && (
                                  <button
                                    onClick={() => handleSendEmail(inv)}
                                    disabled={sendingEmailId === inv.id}
                                    className="px-3 py-1.5 rounded-lg bg-blue-500/10 hover:bg-blue-500/20 text-blue-700 text-xs font-medium disabled:opacity-50 flex items-center space-x-1 transition-colors mx-auto"
                                  >
                                    {sendingEmailId === inv.id
                                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                      : <Mail className="w-3.5 h-3.5" />}
                                    <span>{inv.lastEmailedAt ? 'Resend' : 'Send Email'}</span>
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-black/[0.02] border-t border-black/[0.06]">
                          <tr>
                            <td colSpan={4} className="px-4 py-2 text-xs text-[#94a3b8]">{filtered.length} invoice{filtered.length !== 1 ? 's' : ''}</td>
                            <td className="px-4 py-2 text-right font-semibold text-[#0f172a]">
                              {fmt(filtered.reduce((s, i) => s + i.totalAmount, 0))}
                            </td>
                            <td />
                            <td />
                            <td />
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  );
                })()}

                {/* Invoice viewer — always visible; click a row or enter an ID to load */}
                <div className="glass-card p-6">
                  <div className="flex items-center justify-between mb-5 print:hidden">
                    <div className="flex items-center space-x-3">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-purple-500/10">
                        <FileText className="w-5 h-5 text-purple-600" />
                      </div>
                      <h2 className="text-lg font-semibold text-[#0f172a]">Invoice Viewer</h2>
                    </div>
                    {viewedInvoice && (
                      <button
                        onClick={handlePrint}
                        className="flex items-center space-x-2 px-4 py-2 rounded-xl bg-[#0f172a] hover:bg-[#1e293b] text-white text-sm font-medium transition-colors"
                      >
                        <Printer className="w-4 h-4" />
                        <span>Print / Save as PDF</span>
                      </button>
                    )}
                  </div>

                  {/* Manual lookup form */}
                  <form onSubmit={handleInvoiceLookup} className="flex space-x-2 mb-6 print:hidden">
                    <input
                      type="text"
                      value={invoiceLookupId}
                      onChange={(e) => setInvoiceLookupId(e.target.value)}
                      placeholder="Enter NetSuite invoice ID to look up…"
                      className="flex-1 px-3 py-2.5 text-sm rounded-xl border border-black/[0.10] bg-white/80 focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30"
                    />
                    <button
                      type="submit"
                      disabled={invoiceLoading || !invoiceLookupId.trim()}
                      className="px-4 py-2.5 rounded-xl bg-[#c41e3a] hover:bg-[#a51830] text-white text-sm font-medium disabled:opacity-50 flex items-center space-x-1"
                    >
                      {invoiceLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                      <span>Look Up</span>
                    </button>
                  </form>

                    {invoiceLoading && (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 text-[#c41e3a] animate-spin" />
                      </div>
                    )}

                    {!invoiceLoading && viewedInvoice && (
                      <div ref={printRef} id="invoice-print-area" className="bg-white rounded-xl border border-black/[0.10] overflow-hidden print:rounded-none print:border-0 font-sans">
                        <div className="p-8 print:p-6 text-sm text-[#1a1a1a]">

                          {/* ── Header: logo left, invoice meta right ── */}
                          <div className="flex justify-between items-start mb-6">
                            <div>
                              <img src="/aaslogo.png" alt="AutoAccess Solutions" className="h-16 mb-3" />
                              <div className="font-bold text-sm">Powered By All State Title Service, Inc</div>
                              <div className="text-sm leading-relaxed mt-1">
                                1579 Hwy 44 E, 2nd Floor<br />
                                PO Box 248<br />
                                Shepherdsville KY 40165
                              </div>
                            </div>

                            {/* Invoice # / Date / Due table */}
                            <table className="border-collapse text-sm min-w-[260px]">
                              <tbody>
                                <tr>
                                  <td className="border border-black/[0.25] px-4 py-2 text-[#555]">Invoice #</td>
                                  <td className="border border-black/[0.25] px-4 py-2 font-bold text-lg">{viewedInvoice.tranId || viewedInvoice.id}</td>
                                </tr>
                                <tr>
                                  <td className="border border-black/[0.25] px-4 py-2 text-[#555]">Invoice Date</td>
                                  <td className="border border-black/[0.25] px-4 py-2">{fmtDate(viewedInvoice.tranDate)}</td>
                                </tr>
                                <tr>
                                  <td className="border border-black/[0.25] px-4 py-2 text-[#555]">Due Date</td>
                                  <td className="border border-black/[0.25] px-4 py-2">{fmtDate(viewedInvoice.tranDate)}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>

                          {/* ── Bill To / Billing Period ── */}
                          <table className="w-full border-collapse mb-6">
                            <thead>
                              <tr>
                                <th className="border border-black/[0.25] px-4 py-2 text-left font-bold w-1/2">Bill To</th>
                                <th className="border border-black/[0.25] px-4 py-2 text-left font-bold w-1/2">Billing Period</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td className="border border-black/[0.25] px-4 py-3 align-top">
                                  <div>{billToName || '—'}</div>
                                  {viewedAddress?.addr1 && <div>{viewedAddress.addr1}</div>}
                                  {viewedAddress?.addr2 && <div>{viewedAddress.addr2}</div>}
                                  {(viewedAddress?.city || viewedAddress?.state || viewedAddress?.zip) && (
                                    <div>{[viewedAddress.city, viewedAddress.state, viewedAddress.zip].filter(Boolean).join(' ')}</div>
                                  )}
                                  {viewedAddress?.country && <div>{viewedAddress.country}</div>}
                                </td>
                                <td className="border border-black/[0.25] px-4 py-3 align-middle">
                                  {viewedInvoice.startDate && viewedInvoice.endDate
                                    ? `${fmtDate(viewedInvoice.startDate)} - ${fmtDate(viewedInvoice.endDate)}`
                                    : '—'}
                                </td>
                              </tr>
                            </tbody>
                          </table>

                          {/* ── Line items ── */}
                          <table className="w-full border-collapse mb-6">
                            <thead>
                              <tr>
                                <th className="border border-black/[0.25] px-4 py-2 text-left font-bold">Report Type</th>
                                <th className="border border-black/[0.25] px-4 py-2 text-right font-bold w-20">QTY</th>
                                <th className="border border-black/[0.25] px-4 py-2 text-right font-bold w-28">Rate</th>
                                <th className="border border-black/[0.25] px-4 py-2 text-right font-bold w-28">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(viewedInvoice.item?.items || []).map((line, idx) => (
                                <tr key={idx}>
                                  <td className="border-l border-r border-b border-black/[0.15] px-4 py-2">
                                    {line.description || line.item?.refName || line.item?.id || '—'}
                                  </td>
                                  <td className="border-r border-b border-black/[0.15] px-4 py-2 text-right">{fmtQty(line.quantity)}</td>
                                  <td className="border-r border-b border-black/[0.15] px-4 py-2 text-right">{fmt(line.rate)}</td>
                                  <td className="border-r border-b border-black/[0.15] px-4 py-2 text-right">{fmt(line.amount)}</td>
                                </tr>
                              ))}
                              {(!viewedInvoice.item?.items || viewedInvoice.item.items.length === 0) && (
                                <tr>
                                  <td colSpan={4} className="border border-black/[0.15] px-4 py-6 text-center text-[#94a3b8]">No line items</td>
                                </tr>
                              )}
                            </tbody>
                          </table>

                          {/* ── Footer: contact left, totals right ── */}
                          <div className="flex justify-between items-end gap-6">
                            <div className="text-sm">
                              <div className="flex gap-2 mb-2">
                                <span className="font-medium">For Billing inquiries:</span>
                                <a href="mailto:contact@autoaccesssolutions.org" className="text-[#c41e3a]">contact@autoaccesssolutions.org</a>
                              </div>
                              <div className="font-bold italic">Thank You for your business!</div>
                            </div>

                            <table className="border-collapse text-sm min-w-[260px]">
                              <tbody>
                                <tr>
                                  <td className="border border-black/[0.25] px-4 py-1.5 text-right">Total</td>
                                  <td className="border border-black/[0.25] px-4 py-1.5 text-right">{fmt(viewedInvoice.total)}</td>
                                </tr>
                                <tr>
                                  <td className="border border-black/[0.25] px-4 py-1.5 text-right">Payments/Credits</td>
                                  <td className="border border-black/[0.25] px-4 py-1.5 text-right">$0.00</td>
                                </tr>
                                <tr>
                                  <td className="border border-black/[0.25] px-4 py-1.5 text-right font-bold">Balance Due</td>
                                  <td className="border border-black/[0.25] px-4 py-1.5 text-right font-bold">{fmt(viewedInvoice.amountRemaining ?? viewedInvoice.total)}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>

                        </div>
                      </div>
                    )}

                    {!invoiceLoading && !viewedInvoice && (
                      <div className="rounded-xl border border-dashed border-black/[0.10] py-12 text-center text-[#94a3b8] text-sm">
                        Click an invoice number above or enter a NetSuite invoice ID to view it.
                      </div>
                    )}
                  </div>
              </div>
            )}

            {/* ================================================================ */}
            {/* F) RUNNING TOTALS TAB                                             */}
            {/* ================================================================ */}
            {activeTab === 'totals' && (
              <div className="space-y-4">
                <div className="glass-card p-4 flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-blue-500/10">
                      <BarChart3 className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-[#0f172a]">Running Totals</h2>
                      <p className="text-xs text-[#94a3b8] mt-0.5">Live per-client activity for the current billing period</p>
                    </div>
                  </div>
                  <button
                    onClick={loadRunningTotals}
                    disabled={totalsLoading}
                    className="flex items-center space-x-1.5 px-3 py-2 rounded-xl border border-black/[0.10] bg-white/60 hover:bg-white/90 text-sm text-[#475569] transition-colors disabled:opacity-50"
                  >
                    <RefreshCw className={`w-4 h-4 ${totalsLoading ? 'animate-spin' : ''}`} />
                    <span>Refresh</span>
                  </button>
                </div>

                {totalsLoading && (
                  <div className="flex items-center justify-center py-16">
                    <Loader2 className="w-7 h-7 text-[#c41e3a] animate-spin" />
                  </div>
                )}

                {!totalsLoading && runningTotals.length === 0 && (
                  <div className="glass-card p-10 text-center">
                    <BarChart3 className="w-10 h-10 text-[#94a3b8] mx-auto mb-3" />
                    <p className="text-sm font-medium text-[#64748b]">No activity data available</p>
                    <p className="text-xs text-[#94a3b8] mt-1">Running totals will appear here once clients have activity in the current period.</p>
                  </div>
                )}

                {!totalsLoading && runningTotals.length > 0 && (
                  <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {runningTotals.map((client) => (
                      <div key={client.clientId} className="glass-card p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div className="min-w-0">
                            <h3 className="font-semibold text-[#0f172a] truncate">{client.clientName}</h3>
                            <p className="text-xs text-[#94a3b8] mt-0.5">
                              {fmtDateShort(client.periodStart)} – {fmtDateShort(client.periodEnd)}
                            </p>
                          </div>
                          <div className="text-right ml-3 flex-shrink-0">
                            <div className="text-2xl font-bold text-[#0f172a]">{fmt(client.totalAmount)}</div>
                            <div className="text-xs text-[#94a3b8] mt-0.5">total</div>
                          </div>
                        </div>

                        {client.breakdown && client.breakdown.length > 0 && (
                          <div className="border-t border-black/[0.06] pt-3 mt-3 space-y-1.5">
                            <div className="grid grid-cols-4 items-center text-xs font-medium text-[#94a3b8] uppercase tracking-wide mb-2">
                              <span>Service</span>
                              <span className="text-center">Qty</span>
                              <span className="text-right">Price</span>
                              <span className="text-right">Amount</span>
                            </div>
                            {client.breakdown.map((item) => (
                              // Keyed on price too — a tiered service returns one row per rate,
                              // and keying on service alone collides across those rows.
                              <div key={`${item.serviceType}-${item.stateCode ?? ''}-${item.price}`} className="grid grid-cols-4 items-center text-sm">
                                <span className="text-[#64748b] truncate">
                                  {item.stateCode ? `${item.serviceType} (${item.stateCode})` : item.serviceType}
                                </span>
                                <span className="text-center text-[#94a3b8]">{item.count}</span>
                                <span className="text-right text-[#94a3b8]">{fmt(item.price)}</span>
                                <span className="text-right font-medium text-[#0f172a]">{fmt(item.amount)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>

      <Footer />

      <ConfirmDialog {...confirmDialogProps} />

      {/* ================================================================ */}
      {/* Item picker modal                                                  */}
      {/* ================================================================ */}
      {showItemPicker && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 print:hidden">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl max-w-lg w-full max-h-[80vh] flex flex-col border border-black/[0.08] shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-black/[0.08]">
              <h3 className="text-lg font-bold text-[#0f172a]">Select Catalog Item</h3>
              <button onClick={() => { setShowItemPicker(false); setItemPickerFilter(''); }}
                className="p-2 hover:bg-black/[0.05] rounded-lg transition-colors text-[#64748b]">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 border-b border-black/[0.06] space-y-3">
              <input
                autoFocus
                type="text"
                value={itemPickerFilter}
                onChange={(e) => setItemPickerFilter(e.target.value)}
                placeholder="Filter by name or type..."
                className="w-full px-3 py-2.5 text-sm rounded-xl border border-black/[0.10] bg-white/80 focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/30"
              />
              {selectedCustomerId && (
                <p className="text-xs text-[#64748b] bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 leading-relaxed">
                  Prices shown are catalog defaults. The rate will automatically update to this client's pricing after you select an item.
                </p>
              )}
            </div>

            <div className="overflow-y-auto flex-1 p-2">
              {catalogLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 text-[#c41e3a] animate-spin" />
                </div>
              ) : filteredPickableItems.length === 0 ? (
                <p className="text-center text-[#94a3b8] py-8 text-sm">No items found</p>
              ) : (
                filteredPickableItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleAddItem(item)}
                    className="w-full text-left px-4 py-3 rounded-xl hover:bg-[#c41e3a]/5 transition-colors flex items-center justify-between group"
                  >
                    <div>
                      <div className="font-medium text-sm text-[#0f172a] group-hover:text-[#c41e3a] transition-colors">
                        {item.label}
                      </div>
                      <div className="text-xs text-[#94a3b8] mt-0.5">{item.source}</div>
                    </div>
                    <div className="text-sm font-semibold text-[#0f172a] ml-4 flex-shrink-0">
                      {item.price > 0 ? fmt(item.price) : '—'}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* ================================================================ */}
      {/* Print stylesheet                                                   */}
      {/* ================================================================ */}
      <style>{`
        @media print {
          body > * { display: none !important; }
          #invoice-print-area,
          #invoice-print-area * { display: revert !important; }
          #invoice-print-area {
            position: fixed;
            inset: 0;
            background: white;
            padding: 32px;
            z-index: 9999;
          }
        }
      `}</style>
    </div>
  );
};

export default Invoicing;
